import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  PaymentRequestButtonElement
} from "@stripe/react-stripe-js"
import {
  attachUserPaymentMethod,
  failedPaymentMethods,
  requestedPaymentMethods,
  paymentMethodsSelector,
  setSelectedPaymentMethodId,
  selectedPaymentMethodIdSelector,
  getPaymentMethods,
  checkStripeFullyConnected
} from "store/user"
import { countries } from "country-codes-flags-phone-codes"
import { routes } from "router"
import { createPaymentIntent, capturePayment, updateEmbeddededBooking } from "store/experiences"
import { Form, Radio, Typography, Input, Select, Row, Col, Divider, message, Button } from "antd"
import styles from "./ExperienceBooking.module.css"
import AddCardModal from "../../Bookings/BookingForm/AddCardModal"
import AddCard from "assets/icons/card_icon.png"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import { numberToCurrency } from "helpers/string"
import PaypalPayment from "./PaypalPayment"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"

const { Text, Title } = Typography
const { Option } = Select

const PaymentBlock = ({ form, uuid, setPaymentLoading, mode }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { Paragraph } = Typography
  const accessToken = localStorage.getItem("access_token")
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const {
    experienceById: { experienceDetail },
    experienceBookingData: { bookingSelections },
    bookingInfo
  } = useSelector((state) => state.experiences)

  const paymentMethods = useSelector(paymentMethodsSelector)
  const userData = useSelector((state) => state?.user)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(selectedPaymentMethodId)

  const stripe = useStripe()
  const elements = useElements()

  const [paymentRequest, setPaymentRequest] = useState(null)
  const [canMakePayment, setCanMakePayment] = useState(false)
  const [paymentInfo, setPaymentInfo] = useState(null)

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isAddingCard, setIsAddingCard] = useState(false)

  const depositAmount = ((bookingSelections?.amount || 0) * (experienceDetail?.deposit_percentage || 0)) / 100
  const balanceAmount = (bookingSelections?.amount || 0) - depositAmount

  const isPaypalConnected = experienceDetail?.guide?.paypal_account_data?.is_connected || false
  const isStripeConnected = checkStripeFullyConnected(experienceDetail?.guide?.stripe_account_data)

  useEffect(() => {
    if (stripe && elements && bookingSelections?.amount) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Total",
          amount: Math.round(bookingSelections?.amount * 100)
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ["link"]
      })

      pr.canMakePayment().then(async (result) => {
        if (result) {
          setCanMakePayment(true)
          setPaymentRequest(pr)

          try {
            dispatch(createPaymentIntent(bookingInfo?.data?.id, bookingInfo?.data?.guest_token)).then((res) => {
              setPaymentInfo({ client_secret: res?.client_secret, income_id: res?.income?.id })
            })
          } catch (err) {
            console.error("Failed to get client_secret", err)
          }
        }
      })
    }
  }, [stripe, elements, bookingSelections?.amount, bookingInfo?.data?.guest_token, bookingInfo?.data?.id, dispatch])

  useEffect(() => {
    if (paymentRequest && paymentInfo?.client_secret && stripe) {
      paymentRequest.on("paymentmethod", async (ev) => {
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          paymentInfo.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        )

        if (confirmError) {
          ev.complete("fail")
          console.error("Payment confirmation failed:", confirmError)
          return
        }

        ev.complete("success")

        if (paymentIntent.status === "requires_action") {
          const { error } = await stripe.confirmCardPayment(paymentInfo?.client_secret)
          if (error) {
            console.error("Action failed:", error)
            return
          }
          return
        }

        if (!paymentIntent.id) return
        setPaymentLoading(true)
        dispatch(capturePayment(bookingInfo?.data?.id, paymentInfo?.income_id)).then((captureRes) => {
          if (captureRes.status === "succeeded" || captureRes.status === "requested") {
            if (captureRes.status === "succeeded") {
              message.success("Booking done successfully!")
            }
            form.resetFields()
            navigate(routes?.embeddedExperienceBookingConfirmation({ uuid, id: bookingInfo?.data?.uuid }))
          }
          setPaymentLoading(false)
        })
      })
    }
  }, [
    paymentRequest,
    paymentInfo,
    stripe,
    uuid,
    bookingInfo?.data?.id,
    bookingInfo?.data?.uuid,
    dispatch,
    form,
    navigate,
    setPaymentLoading
  ])

  useEffect(() => {
    if (accessToken && mode) {
      dispatch(getPaymentMethods(userData?.id))
    }
  }, [accessToken, mode, userData?.id, dispatch])

  useEffect(() => {
    const defaultCard = paymentMethods?.find((card) => card.is_default === true)

    if (defaultCard) {
      setDefaultPaymentMethodId(defaultCard.payment_method_id)
    }
  }, [paymentMethods, dispatch])

  const onAttachHandler = (paymentMethodId) => () => {
    dispatch(setSelectedPaymentMethodId(paymentMethodId))
    for (const element of elements._elements) {
      element.clear()
    }
    dispatch(getPaymentMethods(userData?.id))
    onCancelModal()
  }

  const handleAddCard = async () => {
    if (!stripe || !elements) {
      message.error("Stripe is not loaded properly. Please try again later.")
      return false
    }

    let formData
    try {
      formData = await form.validateFields()
    } catch (err) {
      console.error(err)
      message.error("Please fill all required fields correctly.")
      return
    }

    const cardNumberElement = elements.getElement(CardNumberElement)

    if (!cardNumberElement) {
      message.error("Card input is not initialized. Try refreshing the page.")
      return
    }

    const param = {
      type: "card",
      card: cardNumberElement,
      billing_details: {
        name: formData?.cardHolderName,
        address: {
          postal_code: formData?.zip
        }
      }
    }
    dispatch(requestedPaymentMethods())
    setIsAddingCard(true)

    try {
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod(param)

      if (stripeError) {
        dispatch(failedPaymentMethods())

        if (stripeError.type === "card_error") {
          message.error(stripeError.message || "Card error. Please check your card details.")
        } else {
          message.error("Payment method creation failed. Try again.")
        }

        setIsAddingCard(false)
        return false
      }

      const payload = {
        id: userData?.id,
        payment_method_id: paymentMethod.id
      }

      dispatch(attachUserPaymentMethod(payload)).then((result) => {
        if (result?.error) {
          message.error("Failed to attach card to your profile.")
        } else {
          onAttachHandler(paymentMethod.id)()
          message.success("Card added successfully.")
        }
      })
    } catch (err) {
      console.error(err)
      dispatch(failedPaymentMethods())
      message.error("An unexpected error occurred. Please try again later.")
    } finally {
      setIsAddingCard(false)
    }
  }

  const onCancelModal = () => {
    setIsModalVisible(false)
  }

  const getRadioClassName = (value) => {
    if (selectedPayment === value) {
      return `${styles.radioReverse} ${styles.active}`
    }
    return styles.radioReverse
  }

  const renderSavedPaymentMethods = () => {
    if (paymentMethods?.length === 0) {
      return (
        <div className={styles.container}>
          <img src={AddCard} alt="Add Card" className={styles.image} />
          <Paragraph className={styles.text}>You have no payment methods added yet</Paragraph>
          <Button
            variant="link"
            color="primary"
            className={styles.addButton}
            onClick={() => {
              setIsModalVisible(true)
            }}
          >
            Add card
          </Button>
        </div>
      )
    }

    return (
      <>
        <PaymentMethodList
          active={defaultPaymentMethodId}
          paymentMethods={paymentMethods}
          className="vstack gap-15"
          setActive={setDefaultPaymentMethodId}
        />
        <Button
          variant="link"
          color="primary"
          onClick={() => setIsModalVisible(true)}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}
        >
          Add card
        </Button>
      </>
    )
  }

  const renderPaymentSection = () => {
    if (mode && accessToken) {
      return (
        <>
          {renderSavedPaymentMethods()}
          <AddCardModal
            visible={isModalVisible}
            form={form}
            loading={isAddingCard}
            onCancel={() => onCancelModal()}
            onSubmit={() => handleAddCard()}
          />
        </>
      )
    }

    if (isStripeConnected) {
      return (
        <>
          <Form.Item label="Name On Card" name="cardName" rules={[{ required: true, message: "Please enter the name on card" }]}>
            <Input placeholder="Enter full name on card" />
          </Form.Item>
          <Form.Item label="Card Number" name="cardNumber" rules={[{ required: true, message: "Please enter your card number" }]}>
            <div className={styles.stripeInputs}>
              <CardNumberElement
                options={{ showIcon: true }}
                onChange={(value) => {
                  if (value.complete) {
                    form.setFieldValue("cardNumber", true)
                  }
                }}
              />
            </div>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Expiration Date" name="expiry" rules={[{ required: true, message: "Enter expiry date" }]}>
                <div className={styles.stripeInputs}>
                  <CardExpiryElement
                    onChange={(value) => {
                      if (value.complete) {
                        form.setFieldValue("expiry", true)
                      }
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="CVC" name="cvc" rules={[{ required: true, message: "Enter CVC" }]}>
                <div className={styles.stripeInputs}>
                  <CardCvcElement
                    className={styles.cardInput}
                    onChange={(value) => {
                      if (value.complete) {
                        form.setFieldValue("cvc", true)
                      }
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Country"
                name="country"
                initialValue="United States"
                rules={[{ required: true, message: "Select a country" }]}
              >
                <Select placeholder="Choose a country">
                  {countries?.map((country) => (
                    <Option key={country.code} value={country.name}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="ZIP Code" name="zip" rules={[{ required: true, message: "Enter ZIP code" }]}>
                <Input placeholder="ZIP Code" />
              </Form.Item>
            </Col>
          </Row>
        </>
      )
    }

    return null
  }

  return (
    <>
      <Title level={5} className="mb-m">
        How would you like to pay?
      </Title>
      <Form.Item name="paymentMethod" initialValue="full" style={{ marginBottom: 32 }}>
        <Radio.Group
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
          onChange={(e) => {
            const selected = e?.target?.value
            let payment_option = "full_amount_today"

            if (selected === "later") {
              payment_option = "deposit_and_balance_later"
            } else if (selected === "cash") {
              payment_option = "deposit_and_balance_in_cash"
            }

            dispatch(
              updateEmbeddededBooking(bookingInfo?.data?.uuid, {
                booking: { payment_option }
              })
            )
          }}
        >
          <Radio className={getRadioClassName("full")} value="full">
            <div>
              <Text strong>Pay the total amount today</Text>
              <br />
              <Text type="secondary">
                Your card will be charged the total amount <b>{numberToCurrency(bookingSelections?.amount)}</b> today.
              </Text>
            </div>
          </Radio>
          {!experienceDetail?.full_payment_required && !bookingInfo?.data?.less_then_24h && (
            <>
              <Radio className={getRadioClassName("cash")} value="cash">
                <div>
                  <Text strong>Pay deposit today & the balance in cash</Text>
                  <br />
                  <Text type="secondary">
                    Your card will be charged {numberToCurrency(depositAmount)} now & {numberToCurrency(balanceAmount)} in cash on the day
                    of trip.
                  </Text>
                </div>
              </Radio>
              <Radio className={getRadioClassName("later")} value="later">
                <div>
                  <Text strong>Pay deposit today & the balance later</Text>
                  <br />
                  <Text type="secondary">
                    Your card will be charged {numberToCurrency(depositAmount)} now & {numberToCurrency(balanceAmount)} 24h before your
                    trip.
                  </Text>
                </div>
              </Radio>
            </>
          )}
        </Radio.Group>
      </Form.Item>
      <Title level={5} className="mb-m">
        {t("booking.payment_methods")}
      </Title>
      {((canMakePayment && paymentRequest && selectedPayment === "full") || (mode && accessToken) || isStripeConnected) && (
        <div className={styles.paymentProviderGroup}>
          {canMakePayment && paymentRequest && selectedPayment === "full" && (
            <>
              <div style={{ marginBottom: 24 }}>
                <PaymentRequestButtonElement
                  options={{ paymentRequest }}
                  style={{
                    paymentRequestButton: {
                      type: "default",
                      theme: "dark",
                      height: "40px",
                      width: "100%"
                    }
                  }}
                />
              </div>
              <Divider plain className="my-s">
                or
              </Divider>
            </>
          )}
          {renderPaymentSection()}
        </div>
      )}
      {isPaypalConnected && isFeatureEnabled('PAYPAL_SUPPORT') && (
        <div className={styles.paymentProviderGroup}>
          <PaypalPayment bookingId={bookingInfo?.data?.id} bookingUuid={bookingInfo?.data?.uuid} />
        </div>
      )}
    </>
  )
}

PaymentBlock.propTypes = {
  form: PropTypes.object,
  uuid: PropTypes.string,
  setPaymentLoading: PropTypes.func,
  mode: PropTypes.string
}

export default PaymentBlock
